import React from 'react';
import PropTypes from 'prop-types';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { InfoTooltipButton } from './InfoTooltipButton';
import './MetricComparisonCard.css';

export function MetricComparisonCard({
  icon,
  iconColor,
  iconBg,
  title,
  campoValue,
  retroValue,
  unit,
  tooltipContent,
  comparisonSubtitle,
  formatValue = (v) => v.toFixed(1),
}) {
  const { t } = useTranslation();
  const delta = retroValue - campoValue;
  const deltaAbs = Math.abs(delta);
  const isDeltaPositive = delta > 0;
  const isDeltaZero = Math.abs(delta) < 1e-9;

  const campoAbs = Math.abs(campoValue || 0);
  const retroAbs = Math.abs(retroValue || 0);
  const maxAbs = Math.max(campoAbs, retroAbs, 0);
  const campoPct = maxAbs > 0 ? (campoAbs / maxAbs) * 100 : 0;
  const retroPct = maxAbs > 0 ? (retroAbs / maxAbs) * 100 : 0;
  
  return (
    <div className="metric-comparison-card">
      {/* Header */}
      <div className="metric-comparison-card__header">
        <div className="metric-comparison-card__title-group">
          <div
            className="metric-comparison-card__icon"
            style={{ background: iconBg, color: iconColor }}
          >
            {icon}
          </div>
          <h3 className="metric-comparison-card__title">{title}</h3>
        </div>
        <InfoTooltipButton content={tooltipContent} />
      </div>
      
      {/* Current Values */}
      <div className="metric-comparison-card__values">
        <div className="metric-comparison-card__value-col">
          <span className="metric-comparison-card__label metric-comparison-card__label--campo">
            {t('analytics.labels.field')}
          </span>
          <span className="metric-comparison-card__value metric-comparison-card__value--campo">
            {formatValue(campoValue)}{unit}
          </span>
        </div>
        
        <div className="metric-comparison-card__value-col metric-comparison-card__value-col--delta">
          <span className="metric-comparison-card__label metric-comparison-card__label--delta">
            Δ
          </span>
          <div className="metric-comparison-card__delta-group">
            <span className="metric-comparison-card__value metric-comparison-card__value--delta">
              {isDeltaZero ? formatValue(0) : `${isDeltaPositive ? '+' : '-'}${formatValue(deltaAbs)}`}{unit}
            </span>
            {isDeltaZero ? null : isDeltaPositive ? (
              <ArrowUp size={14} className="metric-comparison-card__arrow metric-comparison-card__arrow--up" />
            ) : (
              <ArrowDown size={14} className="metric-comparison-card__arrow metric-comparison-card__arrow--down" />
            )}
          </div>
        </div>
        
        <div className="metric-comparison-card__value-col">
          <span className="metric-comparison-card__label metric-comparison-card__label--retro">
            {t('analytics.labels.underPanel')}
          </span>
          <span className="metric-comparison-card__value metric-comparison-card__value--retro">
            {formatValue(retroValue)}{unit}
          </span>
        </div>
      </div>
      
      {/* Period comparison bars */}
      <div className="metric-comparison-card__comparison" aria-label={comparisonSubtitle || undefined}>
        <div className="metric-comparison-card__bar-row">
          <span className="metric-comparison-card__bar-label metric-comparison-card__bar-label--campo">
            {t('analytics.labels.field')}
          </span>
          <div className="metric-comparison-card__bar-track" aria-hidden="true">
            <div
              className="metric-comparison-card__bar metric-comparison-card__bar--campo"
              style={{ width: `${campoPct}%` }}
            />
          </div>
          <span className="metric-comparison-card__bar-value">
            {formatValue(campoValue)}{unit}
          </span>
        </div>

        <div className="metric-comparison-card__bar-row">
          <span className="metric-comparison-card__bar-label metric-comparison-card__bar-label--retro">
            {t('analytics.labels.underPanel')}
          </span>
          <div className="metric-comparison-card__bar-track" aria-hidden="true">
            <div
              className="metric-comparison-card__bar metric-comparison-card__bar--retro"
              style={{ width: `${retroPct}%` }}
            />
          </div>
          <span className="metric-comparison-card__bar-value">
            {formatValue(retroValue)}{unit}
          </span>
        </div>

        {comparisonSubtitle ? (
          <div className="metric-comparison-card__comparison-subtitle">
            {comparisonSubtitle}
          </div>
        ) : null}
      </div>
    </div>
  );
}

MetricComparisonCard.propTypes = {
  icon: PropTypes.node.isRequired,
  iconColor: PropTypes.string.isRequired,
  iconBg: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  campoValue: PropTypes.number.isRequired,
  retroValue: PropTypes.number.isRequired,
  unit: PropTypes.string.isRequired,
  tooltipContent: PropTypes.string.isRequired,
  comparisonSubtitle: PropTypes.string,
  formatValue: PropTypes.func,
};
